/**
 * <brag-bar> — seamless auto-scrolling logo marquee (sections/brag-bar.liquid).
 *
 * The section renders ONE group of logos (content_for 'blocks'). To loop seamlessly with a
 * pure-CSS -50% translate, the track needs an even number of IDENTICAL groups whose combined
 * width comfortably exceeds the viewport. This element clones the original group to reach that,
 * then sets --brag-duration from the merchant's px/sec speed so the pace is consistent no
 * matter how many logos are added. Rebuilds on resize and after images load (their widths
 * change the loop math). Direction / pause-on-hover / reduced-motion are handled in CSS.
 */
const debounce = (fn, ms) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

class BragBar extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-brag-track]');
    this.group = this.querySelector('[data-brag-group]');
    if (!this.track || !this.group) return;

    this.speed = Math.max(5, parseFloat(this.dataset.speed) || 60); // px per second
    this.rebuild = debounce(() => this.build(), 150);

    this.build();

    // Logo images may not have their width yet on first paint — rebuild once each loads.
    this.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', this.rebuild, { once: true });
    });

    // Keep the loop seamless across viewport changes.
    this.resizeObserver = new ResizeObserver(this.rebuild);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  build() {
    // Clear any previous clones before re-measuring.
    this.track.querySelectorAll('[data-brag-clone]').forEach((n) => n.remove());

    const viewport = this.clientWidth;
    const groupWidth = this.group.scrollWidth;
    if (!groupWidth || !viewport) return;

    // Enough identical groups to exceed 2× the viewport, forced even so the two halves match.
    let copies = Math.max(2, Math.ceil((viewport * 2) / groupWidth));
    if (copies % 2 !== 0) copies += 1;

    for (let i = 1; i < copies; i++) {
      const clone = this.group.cloneNode(true);
      clone.setAttribute('data-brag-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-brag-group');
      this.#stripEditorAttrs(clone);
      this.track.appendChild(clone);
    }

    // -50% of the track = (copies / 2) groups. Duration so that distance passes at `speed` px/s.
    const distance = (groupWidth * copies) / 2;
    this.track.style.setProperty('--brag-duration', `${(distance / this.speed).toFixed(2)}s`);
  }

  // Clones must not carry theme-editor ids/bindings, or the editor would treat them as real
  // blocks (double selection / duplicate ids). Only the original group stays editable.
  #stripEditorAttrs(root) {
    const clean = (el) => {
      if (!el.attributes) return;
      [...el.attributes].forEach((a) => {
        if (a.name === 'id' || /^data-shopify/i.test(a.name)) el.removeAttribute(a.name);
      });
    };
    clean(root);
    root.querySelectorAll('*').forEach(clean);
  }
}

if (!customElements.get('brag-bar')) {
  customElements.define('brag-bar', BragBar);
}
